import type { LedgerEntry } from '@vibetime/runtime';
import type { TimelineIndex } from './dag.js';
import { prefixUpTo } from './dag.js';
import { stateAt } from './state.js';

/**
 * A fork manifest is everything the runner needs to resume a new run
 * starting from an existing checkpoint: the parent run, the entry id
 * it branches from, and any overrides the user supplied (model swap,
 * next-prompt rewrite). The runner consumes this via its existing
 * resume path — it replays the prefix from the parent run, then takes
 * its next instruction from this manifest instead of the original.
 */
export interface ForkManifest {
  version: 1;
  parentRunId: string;
  parentEntryId: string;
  /** New run id the caller should mint. */
  forkRunId: string;
  /** Optional model override for the next step. */
  modelOverride?: string;
  /** Optional rewritten next-message / next-prompt. */
  nextPrompt?: string;
  /** Goal inherited from the parent run (for display only). */
  goal: string | null;
  /** ISO timestamp of fork creation. */
  createdAt: string;
  /** Parent chain prefix, ready to be replayed by the runner. */
  parentPrefix: LedgerEntry[];
  /** Summary of the state at the checkpoint, for UIs. */
  checkpointSummary: {
    entryIndex: number;
    currentModelId: string | null;
    costUsd: number;
    openToolCalls: number;
    planTasks: number;
  };
}

export interface ForkOptions {
  parentRunId: string;
  parentEntryId: string;
  forkRunId: string;
  modelOverride?: string;
  nextPrompt?: string;
  now?: () => Date;
}

export function buildForkManifest(index: TimelineIndex, opts: ForkOptions): ForkManifest {
  const parentPrefix = prefixUpTo(index, opts.parentEntryId);
  if (parentPrefix.length === 0) {
    throw new Error(`unknown checkpoint: ${opts.parentEntryId}`);
  }
  const snapshot = stateAt(index, opts.parentEntryId);
  const now = (opts.now ?? (() => new Date()))();
  return {
    version: 1,
    parentRunId: opts.parentRunId,
    parentEntryId: opts.parentEntryId,
    forkRunId: opts.forkRunId,
    modelOverride: opts.modelOverride,
    nextPrompt: opts.nextPrompt,
    goal: snapshot.goal,
    createdAt: now.toISOString(),
    parentPrefix,
    checkpointSummary: {
      entryIndex: snapshot.entryIndex,
      currentModelId: snapshot.currentModelId,
      costUsd: snapshot.costUsd,
      openToolCalls: snapshot.openToolCalls.length,
      planTasks: snapshot.planTasks.length,
    },
  };
}
